import { find, map } from "lodash-es";
import { useState } from "react";
import type { Key } from "react";

import {
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
  MDSToggleButton,
} from "@/mds";
import { MDSZIndexes } from "@/mds/foundations";

import { StyledSelectMenu } from "./agent-filter-select.style";
import type { IAgentFilterSelectProps } from "./agent-filter-select.types";

export const AgentFilterSelect = ({
  label,
  selectedKey,
  defaultKey,
  items,
  leadingIcon,
  alwaysActive = false,
  onSelectionChange,
  onClear,
}: IAgentFilterSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems: MDSMenuContentItem[] = map(items, item => ({
    id: item.id,
    label: item.label,
    kind: MDSMenuContentItemKind.Item,
    icon: item.iconKind,
    isSelected: item.id === selectedKey,
    selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
    variant: MDSMenuItemVariant.OneLine,
  }));

  const selectedItem = find(items, item => item.id === selectedKey);
  const isActive = alwaysActive || selectedKey !== defaultKey;
  const showClearButton = isActive && !alwaysActive;
  const triggerIcon = leadingIcon ?? selectedItem?.iconKind;
  const triggerLabel = isActive ? (selectedItem?.label ?? label) : label;
  const contentList: MDSMenuContentItem[] = [
    {
      id: "filter-header",
      kind: MDSMenuContentItemKind.Header,
      label: alwaysActive ? "Sort by" : "Filter by",
    },
    ...menuItems,
  ];

  const handleSelectionChange = (keys: "all" | Set<Key>) => {
    if (keys === "all") {
      return;
    }

    const key = Array.from(keys)[0];
    if (key !== undefined) {
      onSelectionChange({ key: String(key) });
    }
  };

  return (
    <MDSMenuDropdown
      isOpen={isOpen}
      popoverProps={{
        "data-test-id": "agent-filter-popover",
        style: { zIndex: MDSZIndexes.ScreenOverlayFloatingContent },
      }}
      onOpenChange={setIsOpen}
    >
      <MDSToggleButton
        slot={MDSMenuDropdownSlotName.Trigger}
        aria-label={`${triggerLabel} ${label}`}
        iconKind={triggerIcon}
        isOpen={isOpen}
        isToggled={isActive}
        label={triggerLabel}
        onClear={
          showClearButton
            ? () => {
                onClear();
                setIsOpen(false);
              }
            : undefined
        }
      />
      {/*
        Closing is driven by `onAction`, not `onSelectionChange`: react-stately
        suppresses the selection callback when the picked option is already
        selected, so re-clicking it would otherwise leave the menu open with
        nothing having happened. `onAction` fires on every press (and on
        Enter/Space), so it is the only reliable "the user chose something"
        signal. `disallowEmptySelection` stops that same press from clearing the
        selection underneath us.
      */}
      <StyledSelectMenu
        slot={MDSMenuDropdownSlotName.Menu}
        aria-label={`${label} options`}
        contentList={contentList}
        disallowEmptySelection
        selectedKeys={[selectedKey]}
        selectionMode="single"
        onAction={() => {
          setIsOpen(false);
        }}
        onSelectionChange={handleSelectionChange}
      />
    </MDSMenuDropdown>
  );
};
